import { useForm } from 'react-hook-form';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Field,
  PageHeader,
  TextField,
  toast,
} from '@eon/ui';
import { resolveApiError, useTranslation } from '@eon/i18n';
import { hooks } from '@eon/api-client';
import { ArrowLeft } from 'lucide-react';

interface InviteForm {
  organizationName: string;
  organizationEmail: string;
  organizationPerson: string;
  customMessage: string;
}

export function UserInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const invite = hooks.useInviteOrganization();
  const { register, handleSubmit, formState } = useForm<InviteForm>({
    defaultValues: {
      organizationName: '',
      organizationEmail: '',
      organizationPerson: '',
      customMessage: '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      const res = await invite.mutateAsync({
        organizationName: values.organizationName.trim(),
        organizationEmail: values.organizationEmail.trim(),
        organizationPerson: values.organizationPerson.trim(),
        customMessage: values.customMessage.trim() || undefined,
      });
      if (res.emailDelivery === 'smtp' && res.emailSent) {
        toast.success(t('user.invite.sentEmail'));
      } else if (res.emailDelivery === 'noop') {
        toast.show({ title: t('user.invite.sent'), description: t('user.invite.sentNoSmtp') });
      } else {
        toast.success(t('user.invite.sent'));
      }
      navigate({ to: '/users' });
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  });

  return (
    <>
      <PageHeader
        title={t('user.invite.title')}
        description={t('user.invite.description')}
        actions={
          <Button variant="outline" asChild>
            <Link to="/users">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('user.back')}
            </Link>
          </Button>
        }
      />
      <form onSubmit={onSubmit}>
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>{t('user.invite.orgTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label={t('user.invite.orgName')}>
              <TextField {...register('organizationName', { required: true })} />
            </Field>
            <Field label={t('user.invite.orgEmail')}>
              <TextField type="email" {...register('organizationEmail', { required: true })} />
            </Field>
            <Field label={t('user.invite.contactPerson')}>
              <TextField {...register('organizationPerson', { required: true })} />
            </Field>
            <Field label={t('user.invite.message')}>
              <TextField {...register('customMessage')} />
            </Field>
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button type="button" variant="outline" asChild>
              <Link to="/users">{t('actions.cancel')}</Link>
            </Button>
            <Button type="submit" disabled={formState.isSubmitting || invite.isPending}>
              {t('user.invite.send')}
            </Button>
          </CardFooter>
        </Card>
      </form>
      <p className="text-muted-foreground mt-4 text-sm">
        {t('user.invite.orCreate')}{' '}
        <Link to="/users/new" className="text-primary underline">
          {t('user.createTitle')}
        </Link>
      </p>
    </>
  );
}
